import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import JoditEditor from 'jodit-react';
import { toast } from 'react-toastify';
import { strings } from '/imports/strings';
import { Button } from '/imports/ui/Button';
import { Modal, ModalProps } from '/imports/ui/Modal';
import { Form } from '/imports/ui/Form';
import { LoadingFlex } from '/imports/ui/LoadingFlex';
import { useFindOne, useMethod, useUser } from '/imports/hooks';
import { modelClass } from '/imports/api/solarBills';
import { FormField } from '/imports/ui/FormField';
import { Input } from '/imports/ui/Input';
import { css } from '/imports/ui/css';
import { Checkbox } from '/imports/ui/Checkbox';
import { toastOnError } from '/imports/ui/utils';
import { sendNorthshoreBills } from '/imports/api/northshoreBilling';
import { Orgs } from '/imports/api/orgs';

const NorthShoreSendBillsModalStyled = styled(Modal)`
  .modal-window {
    width: 45rem;
    max-width: 100%;
    max-height: 100%;
    .jodit-container {
      border: ${css.inputBorder};
    }
    .test {
      margin-top: 1rem;
      margin-bottom: 0.5rem;
      input {
        position: relative;
        left: -3px;
        top: -4px;
        margin-right: 5px;
      }
    }
  }
`;

interface NorthShoreSendBillsModalProps extends ModalProps {
  billIds: string[];
}

/** Modal for composing and sending Northshore solar production bill emails. */
export const NorthShoreSendBillsModal = (props: NorthShoreSendBillsModalProps) => {
  const { billIds } = props;
  const user = useUser();
  const orgId = user?.orgId;
  const org = useFindOne(() => Orgs.find(orgId), [orgId]);
  const [result, loading] = useMethod(modelClass.listMethod, { query: { _id: { $in: billIds } }, limit: billIds.length }, [billIds.join(',')]);
  const bills = result?.documents;
  const [subject, setSubject] = useState('Summary of your Solar Production Bills');
  const [html, setHtml] = useState('');
  const [ccEmails, setCcEmails] = useState<string>();
  const [test, setTest] = useState(true);
  const [accessCode, setAccessCode] = useState<string>();
  const [sending, setSending] = useState(false);
  useEffect(() => {
    if (!bills?.length) return;
    setHtml(defaultMessage());
  }, [bills?.length]);
  useEffect(() => {
    if (!org._id) return;
    setAccessCode(org.billingAccessCode);
  }, [org?._id]);
  const send = toastOnError(async () => {
    setSending(true);
    const num = await sendNorthshoreBills.call({ billIds, subject, html, ccEmails, test, accessCode });
    setSending(false);
    toast.success(`${strings.Sent()} ${num}`);
    props.onClose?.();
  });
  return (
    <NorthShoreSendBillsModalStyled
      {...props}
      title={`${strings.SendBills()} - Northshore`}
      onClose={props.onClose}
      body={
        loading || sending ?
          <LoadingFlex caption={sending ? strings.Sending() : ''} />
          :
          <div>
            <div>{strings.YouAreSendingNumBills(bills?.length || 0)}</div>
            <hr />
            <Form onSubmit={() => { }}>
              <FormField field="subject" className="checkbox test">
                <label><Checkbox id="ns-test" checked={test} onChange={(checked) => setTest(checked)} />{strings.Test()}</label>
              </FormField>
              <div className="field-group">
                <FormField field="subject">
                  <label>{strings.Subject()}</label>
                  <Input id="ns-subject" value={subject} onChange={(subject) => setSubject(subject || '')} />
                </FormField>
              </div>
              <div className="field-group">
                <FormField field="html">
                  <label>{strings.Message()}</label>
                  <JoditEditor value={html} onChange={(html) => setHtml(html)} />
                </FormField>
              </div>
              <div className="field-group">
                <FormField field="ccEmails">
                  <label>{strings.CcEmails()} ({strings.CommaSeparated()})</label>
                  <Input id="ns-cc" value={ccEmails} onChange={(ccEmails) => setCcEmails(ccEmails)} />
                </FormField>
              </div>
              <div className="field-group">
                <FormField field="accessCode">
                  <label>{strings.AccessCode()}</label>
                  <Input id="ns-access-code" value={accessCode} onChange={(accessCode) => setAccessCode(accessCode)} />
                </FormField>
              </div>
              <div className="buttons">
                <Button id="ns-send-btn" onClick={() => send()}>{strings.SendBills()}</Button>
              </div>
            </Form>
          </div>
      }
    />
  );
};
/**
 * Builds the default bill email HTML body with placeholder tokens (e.g. __AMOUNT_DUE__).
 * @returns the HTML string
 */
const defaultMessage = () => /*html*/`
<div style="max-width: 600px; font-size: 11pt;">
  <p>Hello</p>
  <p>Your solar production bills are ready.</p>
  <p>
    <b>
      <div>Total Amount Due: __AMOUNT_DUE__</div>
      <div>Statement Date: __STATEMENT_DATE__</div>
      <div>${strings.DueDate()}: __DUE_DATE__</div>
      <div>${strings.BillingPeriod()}: __BILLING_PERIOD__</div>
    </b>
  </p>
  <p>
    <u>Payment will be deducted from your account on file with Solar 24 LLC on __DUE_DATE_2__.</u>
  </p>
  <p>
    <a href="__LINK__">View bill here</a>
  </p>
  <p>Thank You</p>
  <p><b>Luminia Team</b></p>
  <hr />
  <div>
    <img src="https://${window.location.host}/images/logo_dark_193_79.png" style="display: block; max-width: 193px; max-height: 79px;">
  </div>
  <p>
    <div>12707 High Bluff Drive, Suite 115</div>
    <div>San Diego, CA 92130</div>
    <div>P: (858) 866-8777</div>
  </p>
  <p style="font-size: 9pt;">
    NOTICE: This communication and its attachments (if any) have been sent to you from Luminia and contain Privileged, Proprietary and/or Confidential Information. If you are not the intended recipient, you are hereby notified that any disclosure, copying, distribution, or use of the information contained herein (including any reliance thereon) is STRICTLY PROHIBITED. If you are not the intended recipient appearing in the address lines of this communication, please indicate to the sender that you have received this communication in error, and delete the copy you received.
  </p>
</div>
`;